import { Location } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute } from '@angular/router';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import Swal from 'sweetalert2';
import { SousCategorieService } from 'app/services/sous-categorie/sous-categorie.service';
import { ProduitsService } from 'app/services/produits/produits.service';

interface SousProduit {
  id: number;
  name: string;
  image_url: string;
  category: string;
  prix: number;
  displayOrder?: number;
  orderIndex?: number;
  createdAt?: string;
  [key: string]: any;
}

@Component({
  selector: 'app-sous-produits-list',
  templateUrl: './sous-produits-list.component.html',
  styleUrls: ['./sous-produits-list.component.scss']
})
export class SousProduitsListComponent implements OnInit {
  idProduit: any;
  produit: any;
  list: SousProduit[] = [];
  isLoading = false;
  private readonly s3BaseUrl = 'https://tunisie-immob.s3.eu-west-3.amazonaws.com/';

  constructor(
    private sousCategorieService: SousCategorieService,
    private produitsService: ProduitsService,
    private activatedRoute: ActivatedRoute,
    private location: Location
  ) { }

  ngOnInit(): void {
    this.idProduit = this.activatedRoute.snapshot.paramMap.get('id');
    this.loadSousProduits();
  }

  loadSousProduits(): void {
    this.isLoading = true;
    // First get the parent product to have context
    this.produitsService.getProduit(this.idProduit).subscribe({
      next: (product: any) => {
        this.produit = product;
        // Get all sous-categories (or from product.sous_cat)
        this.sousCategorieService.getListSousCategorie().subscribe({
          next: (allSousCats: any[]) => {
            // Filter those belonging to this product
            let sousList = allSousCats.filter(sc => sc.produit_id === parseInt(this.idProduit) || sc.produitId === parseInt(this.idProduit));
            // Initialize order indexes and sort
            this.list = sousList;
            this.initializeOrderIndexes();
            this.isLoading = false;
          },
          error: (err) => {
            console.error(err);
            this.isLoading = false;
            Swal.fire('Erreur', 'Impossible de charger les sous-produits.', 'error');
          }
        });
      },
      error: (err) => {
        console.error(err);
        this.isLoading = false;
        Swal.fire('Erreur', 'Produit introuvable.', 'error');
      }
    });
  }

  private initializeOrderIndexes(): void {
    // Check if any item has displayOrder
    const hasOrder = this.list.some(p => p.displayOrder !== undefined && p.displayOrder !== null);
    if (hasOrder) {
      // Sort by displayOrder ascending
      this.list.sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
    } else {
      // Fallback to createdAt (newest first) or id
      this.list.sort((a, b) => {
        const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return dateB - dateA;
      });
    }
    // Assign sequential orderIndex for UI
    this.list.forEach((item, idx) => {
      item.orderIndex = idx + 1;
      item.displayOrder = idx;
    });
  }

  // Called after any reordering
  private updateOrdersAndSave(): void {
    this.list.forEach((item, idx) => {
      item.orderIndex = idx + 1;
      item.displayOrder = idx;
    });
    this.saveAllOrders();
  }

  private saveAllOrders(): void {
    if (this.list.length === 0) return;
    this.isLoading = true;
    let completed = 0;
    let hasError = false;

    this.list.forEach(sousProduit => {
      this.sousCategorieService.updateSousCategorieOrder(sousProduit.id, sousProduit.displayOrder!).subscribe({
        next: () => {
          completed++;
          if (completed === this.list.length && !hasError) {
            this.isLoading = false;
            Swal.fire({
              toast: true,
              position: 'top-end',
              showConfirmButton: false,
              timer: 1500,
              icon: 'success',
              title: 'Ordre mis à jour'
            });
          }
        },
        error: (err) => {
          console.error(`Error updating order for sous-produit ${sousProduit.id}:`, err);
          hasError = true;
          this.isLoading = false;
          Swal.fire({
            title: 'Erreur',
            text: 'Impossible de sauvegarder l\'ordre. Rechargement...',
            icon: 'error'
          });
          this.loadSousProduits(); // reload to revert
        }
      });
    });
  }

  // Drag & drop
  drop(event: CdkDragDrop<SousProduit[]>): void {
    if (event.previousIndex === event.currentIndex) return;
    moveItemInArray(this.list, event.previousIndex, event.currentIndex);
    this.updateOrdersAndSave();
  }

  moveUp(index: number): void {
    if (index === 0) return;
    [this.list[index], this.list[index - 1]] = [this.list[index - 1], this.list[index]];
    this.updateOrdersAndSave();
  }

  moveDown(index: number): void {
    if (index === this.list.length - 1) return;
    [this.list[index], this.list[index + 1]] = [this.list[index + 1], this.list[index]];
    this.updateOrdersAndSave();
  }

  getMainImageUrl(imageUrlString: string | null | undefined): string | null {
    if (!imageUrlString || imageUrlString.trim() === '') return null;
    const urls = imageUrlString.split(',').map(url => url.trim()).filter(url => url);
    if (urls.length === 0) return null;
    const first = urls[0];
    if (first.startsWith('http')) return first;
    return this.s3BaseUrl + first;
  }

  deleteSousProduit(sousProduit: SousProduit): void {
    Swal.fire({
      title: "Êtes-vous sûr(e) ?",
      text: `Supprimer "${sousProduit.name}" ?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#f44336",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Oui, supprimer",
      cancelButtonText: "Annuler"
    }).then((result) => {
      if (result.isConfirmed) {
        this.sousCategorieService.deleteSousCategorie(sousProduit.id).subscribe({
          next: () => {
            Swal.fire('Supprimé!', 'Le sous-produit a été supprimé.', 'success');
            this.list = this.list.filter(p => p.id !== sousProduit.id);
            this.updateOrdersAndSave(); // re-index after deletion
          },
          error: () => Swal.fire('Erreur', 'Impossible de supprimer.', 'error')
        });
      }
    });
  }

  navigateBack(): void {
    this.location.back();
  }
}